import { Box, IconButton, useToast, Spinner, Stack } from '@chakra-ui/react'
import { useFormContext } from 'react-hook-form'
import { RequestFormData } from '..'
import { useState, useEffect, useRef, useCallback } from 'react'
import ControlInput from '../../../Forms/components/ControlInput'
import TagsDropdown from '../../../Forms/components/TagsDropdown'
import DragAndDrop from '../../../DragAndDrop'
import { useTranslation } from 'react-i18next'
import { chakra } from '@chakra-ui/react'
import type { FC } from 'react'
import { RiMicAiLine as RawMicIcon } from 'react-icons/ri'

const MicIcon = chakra(RawMicIcon as FC)

declare global {
  interface Window {
    webkitSpeechRecognition: any
    SpeechRecognition: any
  }
}

export default function RequestDetailsStep() {
  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext<RequestFormData>()
  const { t } = useTranslation()

  const [files] = useState<File[]>(getValues('files') || [])
  const toast = useToast()
  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef<any>(null)
  const liveTranscript = useRef('')
  const forceStop = useRef(false)

  const initializeRecognition = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.lang = 'fr-FR'
    recognition.continuous = true
    recognition.interimResults = true

    recognition.onstart = () => {
      liveTranscript.current = watch('description') || ''
      setIsListening(true)
    }

    recognition.onend = () => {
      if (!forceStop.current) {
        try {
          recognition.start()
        } catch (err) {
          toast({
            title: 'Redémarrage échoué',
            description: String(err),
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        }
      } else {
        setIsListening(false)
        forceStop.current = false
      }
    }

    recognition.onresult = (event: any) => {
      let final = ''
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript + ' '
        } else {
          interim += transcript
        }
      }
      liveTranscript.current += final
      const fullText = (liveTranscript.current + interim).trim()
      setValue('description', fullText)
    }

    recognition.onerror = (event: any) => {
      toast({
        title: 'Erreur de reconnaissance vocale',
        description: event.error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      setIsListening(false)
    }

    recognitionRef.current = recognition
  }, [setValue, toast, watch])

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      initializeRecognition()
    } else {
      toast({
        title: 'Navigateur non supporté',
        description: 'Web Speech API non supportée dans ce navigateur',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      })
    }
  }, [initializeRecognition, toast])

  const toggleListening = () => {
    const recognition = recognitionRef.current
    if (!recognition) return

    if (!isListening) {
      forceStop.current = false
      recognition.start()
    } else {
      forceStop.current = true
      recognition.stop()
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <ControlInput
        register={{ ...register('title') }}
        label={t('sharedDiagnostic.title')}
        placeholder={t('sharedDiagnostic.titlePlaceholder')}
        isInvalid={!!errors.title}
        errorMessage={errors.title?.message}
      />

      <Box position="relative">
        <ControlInput
          register={{ ...register('description') }}
          label={t('sharedDiagnostic.description')}
          placeholder={t('sharedDiagnostic.descriptionPlaceholder')}
          isInvalid={!!errors.description}
          errorMessage={errors.description?.message}
          textarea
        />
        <IconButton
          icon={
            isListening ? <Spinner size="sm" /> : <MicIcon color="white" boxSize={5} />
          }
          onClick={toggleListening}
          aria-label="Microphone"
          colorScheme={isListening ? 'red' : undefined}
          bg={isListening ? 'red.500' : '#6d3bfe'}
          _hover={{ bg: isListening ? 'red.600' : '#5c2ff5' }}
          position="absolute"
          top="10px"
          right="0px"
          size="sm"
          zIndex={2}
        />
      </Box>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <Box flex={1} minW={0}>
          <TagsDropdown
            values={getValues('tagIds')}
            onChange={(value: number[]) => setValue('tagIds', value)}
            isInvalid={!!errors.tagIds}
            errorMessage={errors.tagIds?.message}
          />
        </Box>
        <Box flex={1} minW={0}>
          <ControlInput
            register={{ ...register('category') }}
            label={t('sharedDiagnostic.category')}
            placeholder={t('sharedDiagnostic.categoryPlaceholder')}
            isInvalid={!!errors.category}
            errorMessage={errors.category?.message}
          />
        </Box>
      </Stack>

      <DragAndDrop
        value={files}
        onFilesSelected={(files) => setValue('files', files)}
        isInvalid={!!errors.files}
        errorMessage={errors.files?.message}
      />
    </Box>
  )
}
